import * as Sentry from '@sentry/browser';
import {
  catchIfThrows,
  ignoreIfThrows,
  prependMessageToError,
} from '@wix/cashier-common/dist/src/utils/errorHandling';
import { WIDGET_BUNDLE_FILE_NAME } from '../../constants';
import { fedopsLogger } from '../fedopsLogger';
import { filterDefined } from '../filterDefined';
import { getAppNameById } from '../getAppNameById';
import { isErrorReportingEnabled, sentryConfig } from './config';

// Given that Widget code is executed in TPAs iframes OR Viewer top frame (OOI context)
// using Sentry directly (without creating a client) might cause internal Sentry issues
// Case study: [at the time of writing] Viewer top frame provides Sentry v4, and Widget's
// Sentry v5 freaks out and loops itself into infinity, freezing the page
const sentryClient = new Sentry.Hub();

const isWidgetEvent = (event: Sentry.Event): boolean => {
  try {
    // if there is no "exception" & "message" exists - it was captured manually within the Widget
    if (!event.exception && event.message) {
      return true;
    }

    const stacktrace =
      event.stacktrace || event.exception?.values[0]?.stacktrace;
    const frames = stacktrace?.frames || [];

    return frames[frames.length - 1].filename.includes(WIDGET_BUNDLE_FILE_NAME);
  } catch (error) {
    return false;
  }
};

export const initErrorReporter = ignoreIfThrows(() => {
  sentryClient.bindClient(
    new Sentry.BrowserClient({
      ...sentryConfig,

      beforeSend(event) {
        if (!isErrorReportingEnabled() || !isWidgetEvent(event)) {
          return null;
        }

        event.fingerprint = event.fingerprint || [
          '{{ error.type }}',
          '{{ error.value }}',
        ];
        fedopsLogger.reportErrorThrownOncePerFlow();
        return event;
      },
    })
  );
});

export const enrichErrorReports = ignoreIfThrows(
  (params: {
    userId: string;
    locale: string;
    appId: string;
    msId: string;
    version: string;
  }) => {
    sentryClient.configureScope((scope) => {
      scope.setTags(
        filterDefined({
          locale: params.locale,
          msId: params.msId,
          appId: params.appId,
          appName: getAppNameById(params.appId),
        })
      );

      if (params.userId) {
        scope.setUser({ id: params.userId });
      }

      if (params.version) {
        scope.addEventProcessor(async (event) => ({
          ...event,
          release: params.version,
        }));
      }
    });
  }
);

export const captureError = (
  error: Error | object | string,
  params?: {
    messagePrefix?: string;
    shouldRethrow?: boolean;
    fingerprint?: string[];
  }
) => {
  const { messagePrefix, shouldRethrow = false, fingerprint } = params || {};

  try {
    sentryClient.withScope((scope) => {
      if (fingerprint) {
        scope.setFingerprint(fingerprint);
      }

      if (error) {
        if (error instanceof Error) {
          let errorToReport: Error = error;

          if (messagePrefix) {
            errorToReport = prependMessageToError(error, messagePrefix);
          }

          sentryClient.captureException(errorToReport);
        } else if (typeof error === 'string') {
          sentryClient.captureMessage(error);
        } else {
          Object.keys(error).forEach((key) => {
            scope.setExtra(key, error[key]);
          });

          sentryClient.captureMessage(
            messagePrefix || 'Manually captured message'
          );
        }
      }
    });
  } catch (error) {
    console.error(`Failed to exec captureError`, error);
  }

  console.error('Captured error:', error);

  if (shouldRethrow) {
    throw error;
  }
};

export const sentry = sentryClient;

export const captureIfThrows: <T extends Function>(
  fn: T,
  messagePrefix?: string
) => T = (fn, messagePrefix) =>
  catchIfThrows(fn, (error) =>
    captureError(error, { messagePrefix, shouldRethrow: true })
  );

export const logDebugMessage = (message: string) => {
  if (process.env.NODE_ENV === 'production') {
    captureError(`[DEBUG] ${message}`);
  }
};
